import {
  Button,
  Card,
  Container,
  Group,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { IconAlertTriangle, IconClock } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import pluralize from "pluralize";
import { useState } from "react";
import SocialShare from "../../common/SocialShare";
import {
  createMember,
  startEvent,
  updateEvent,
  updateMember,
} from "../../common/api";
import { useIsBoardReferee } from "../../common/boards/board.hooks";
import { Board } from "../../common/boards/board.schema";
import {
  Event,
  EventType,
  MAX_MEMBERS,
  MIN_MEMBERS,
  SeedType,
  TournamentType,
} from "../../common/events/event.schema";
import { Member } from "../../common/members/member.schema";
import { PlanType } from "../../common/users/user.schema";
import {
  getBoardPlan,
  getMinTeamMembers,
  getShowClubs,
  getShowCountries,
} from "../../common/util";
import CountryDisplay from "../common/CountryDisplay";
import PlanPromptBar from "../common/PlanPromptBar";
import MemberDisplay from "../members/MemberDisplay";
import TeamsInput from "../members/TeamsInput";
import UserDisplay from "../users/UserDisplay";
import RegistrationsAdmin from "./RegistrationsAdmin";
import SelfRegistration from "./SelfRegistration";
import USFAEventRating from "./USFAEventRating";
import {
  getEventName,
  getHasEliminations,
  getHasPools,
  getHasTeams,
  getIsEndless,
} from "./util";

const SummaryScheduled = ({ event, board }: { event: Event; board: Board }) => {
  const tournMembers = event.members as Member[];
  const tournTeams = event.teams as Member[];
  const { t } = useTranslation();
  const { mutate: doStart, isLoading } = useMutation(startEvent);
  // const now = new Date();
  const queryClient = useQueryClient();
  // const [members, setMembers] = useState(tournMembers);
  const [teams, setTeams] = useState(tournTeams);
  const [teamMembers, setTeamMembers] = useState<string[]>([]);
  // let activeMembers = tournMembers.filter(
  //   (m) => event.withdrawals.indexOf(m._id) === -1
  // );
  const hasTeams = getHasTeams(event);
  // if (hasTeams)
  //   activeMembers = tournTeams.filter(
  //     (m) => event.withdrawals.indexOf(m._id) === -1
  //   );
  // const { board } = useBoard();
  const onClickStart = () => {
    const isFuture =
      event.scheduledDate && dayjs().isBefore(dayjs(event.scheduledDate));
    if (isFuture) {
      openConfirmModal({
        title: t(`${getEventName(event)} is scheduled for future`),
        children: (
          <Text size="sm">
            The scheduled start date for this event is{" "}
            {dayjs(event.scheduledDate).format("MMM D, YYYY")}. Are you sure you
            want to start this event now?
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => {
          start();
        },
      });
    } else {
      start();
    }
  };
  const start = async () => {
    if (!event) return;
    if (teams) {
      const ps: Promise<Member>[] = [];
      teams.map((team) => {
        if (!team._id) {
          ps.push(
            createMember({
              team: true,
              name: team.name,
              board: event.board as string,
              members: team.members as string[],
            })
          );
        } else {
          ps.push(
            updateMember({
              _id: team._id,
              members: team.members as string[],
              name: team.name,
            })
          );
        }
      });
      const updatedTeams = await Promise.all(ps);
      await updateEvent({
        _id: event._id,
        teams: updatedTeams.map((t) => t._id),
      });
    }
    doStart(event._id, {
      onSuccess: () => {
        queryClient.invalidateQueries(["event", event.shortLink]);
      },
    });
  };
  const isRef = useIsBoardReferee();
  const hasPools = getHasPools(event);
  const hasEliminations = getHasEliminations(event);
  const isEndless = getIsEndless(event);
  let memberLimit = board ? getBoardPlan(board).eventSize : 99;
  if (memberLimit < 0) memberLimit = MAX_MEMBERS;
  let teamLimit = board ? getBoardPlan(board).teamLimit : 99;
  if (teamLimit < 0) teamLimit = 128;
  const limited =
    tournMembers.length >= memberLimit &&
    board?.plan === PlanType.FREE &&
    !hasTeams;
  const teamLimited =
    teams.length >= teamLimit && board?.plan === PlanType.FREE && hasTeams;
  // console.log("isAdmin :", isAdmin);
  // console.log("event.started :", event.started);
  // console.log("event.complete :", event.complete);
  // const roundCount = event.eliminationRounds;
  let roundCount = event.eliminations.length;
  // console.log("event.eliminations :", event.eliminations);
  // console.log("roundCount :", roundCount);
  // console.log("event.eliminationRounds :", event.eliminationRounds);
  let elimMemberCount = event.members.length;
  if (hasTeams && hasPools) {
    elimMemberCount = Math.floor(event.members.length / 4);
    // roundCount = Math.floor();
  }
  if (roundCount === 0) {
    while (roundCount < event.eliminationRounds) {
      roundCount++;
      // console.log("roundCount :", roundCount);
      const members = Math.pow(2, roundCount);

      // console.log("members :", members);
      if (members >= elimMemberCount) break;
    }
  }
  const elimMemberName = hasTeams ? "team" : "member";
  // console.log("roundCount :", roundCount);
  let activeCount = 0;
  if (event.registrations)
    activeCount += event.registrations?.filter((r) => {
      let approved = r.approved;
      if (event.requireCheckIn && r.user && !r.checkedIn) approved = false;
      if (event.requireRegistrationPayment && !r.paid) approved = false;
      return approved;
    }).length;
  const isTournament = event.eventType === EventType.TOURNAMENT;
  let canStart = !isTournament;
  if (isTournament) {
    if (event.type === TournamentType.TEAM_POOLS_ELIMINATION)
      activeCount = teams.length;
    // console.log("activeCount :", activeCount);
    if (hasPools && hasTeams && event.teams.length === 0)
      activeCount = event.members.length;
    canStart = activeCount >= MIN_MEMBERS;
    // console.log("canStart :", canStart);
    // console.log("activeCount :", activeCount);
    if (!hasPools && hasTeams && teams && event) {
      canStart = true;
      // console.log("canStart :", canStart);
      activeCount = teams.length;
      if (teams.length === 0) {
        activeCount = Math.floor(event.registrations.length / 3);
      }
      if (activeCount < 2) canStart = false;
      const teamMembers: string[] = [];
      teams.map((team) => {
        // console.log("team :", team);
        if (!team.members) {
          // console.log("no members");
          canStart = false;
          return;
        }
        team.members.map((tm) => {
          if (teamMembers.includes(tm as string)) {
            // console.log("duplicate members");
            canStart = false;
            return;
          }
          teamMembers.push(tm as string);
        });

        if (
          team.members &&
          team.members?.length < getMinTeamMembers(board.sport)
        ) {
          // teamMembers = [
          //   ...(teamMembers as string[]),
          //   ...(team.members as string[]),
          // ];
          // console.log("not enough members");
          canStart = false;
        }
      });
      // console.log("canStart :", canStart);
    }
  }
  const onChangeTeams = (teams: Member[]) => {
    setTeams(teams);
    const members: string[] = [];
    teams.map((t) => t.members?.map((m) => members.push(m as string)));
    if (members.length !== teamMembers.length) {
      setTeamMembers(members);
    }
  };
  // console.log("canRegister :", canRegister);
  const showClubs = getShowClubs(event.members as Member[]);
  const showCountries = getShowCountries(event.members as Member[]);
  const atCapacity =
    !!event.maxMembers && event.registrations.length >= event.maxMembers;
  // console.log("event :", event);
  // console.log("showClubs :", showClubs);
  const sortedRegistrations = event.registrations?.sort((a, b) => {
    if (
      event.seedType === SeedType.MANUAL ||
      event.seedType === SeedType.RANDOM
    ) {
      return a.order - b.order;
    }
    if (event.seedType === SeedType.SCORE) {
      const aScore = a?.score || 500;
      const bScore = b?.score || 500;
      return bScore - aScore;
    }
    if (event.seedType === SeedType.USFA) {
      const aRating = a.usfaRating || "U";
      const bRating = b.usfaRating || "U";
      if (aRating < bRating) return -1;
      if (aRating > bRating) return 1;
      return 0;
    }
    return 0;
  });
  // console.log('activeCount :', activeCount);
  let shareTitle = event.openRegistration
    ? "Share registration and live results"
    : "Share live results";
  if (!isTournament) shareTitle = "Share event registration";
  // console.log("shareTitle :", shareTitle);
  const shareVisible = !isTournament || event.openRegistration;
  return (
    <Container size="sm" pt={10} px={0}>
      <Card>
        <Stack>
          {event.scheduledDate && (
            <Group>
              <ThemeIcon variant="light">
                <IconClock size={16} />
              </ThemeIcon>
              <Text size="sm">
                {`Scheduled for ${dayjs(event.scheduledDate).format(
                  "MMM D, YYYY, H:mm"
                )}`}
              </Text>
            </Group>
          )}
          <USFAEventRating event={event} />
          {atCapacity && event.maxMembers !== 1 && (
            <Group>
              <ThemeIcon color="yellow" variant="light">
                <IconAlertTriangle size={16} />
              </ThemeIcon>
              <Text size="sm">This event is full</Text>
            </Group>
          )}
          {!event.scheduledDate && (
            <Group>
              <ThemeIcon size="xs" variant="light">
                <IconClock size={16} />
              </ThemeIcon>
              <Text size="sm">{getEventName(event)} waiting to start</Text>
            </Group>
          )}
          {isRef && (
            <Button
              onClick={onClickStart}
              disabled={isLoading || !canStart}
              loading={isLoading}
            >
              {t("Start event")}
            </Button>
          )}
          {(isRef || activeCount > 0) && (
            <>
              {hasPools && hasEliminations && (
                <Text size="sm">
                  {activeCount} {pluralize("member", activeCount)} in pools,
                  followed by elimination rounds
                </Text>
              )}
              {hasPools && !hasEliminations && !isEndless && (
                <Text size="sm">
                  {activeCount} {pluralize("member", activeCount)} in pools
                </Text>
              )}
              {isEndless && (
                <Text size="sm">
                  {activeCount} {pluralize("member", activeCount)} in one
                  endless pool
                </Text>
              )}
              {!hasPools && hasEliminations && (
                <Text size="sm">
                  {activeCount} {pluralize(elimMemberName, activeCount)} in
                  elimination rounds
                </Text>
              )}
            </>
          )}
          {isRef && !canStart && (
            <>
              {event.type !== TournamentType.TEAM && (
                <Text size="sm">
                  {getEventName(event)} needs at least {MIN_MEMBERS} to start
                </Text>
              )}
              {event.type === TournamentType.TEAM && (
                <Text size="sm">
                  {getEventName(event)} needs at least 2 complete teams to start
                </Text>
              )}
            </>
          )}
          {!isRef && (
            <Stack>
              <Title order={4} size="xs">
                {event.registrations.length}{" "}
                {pluralize(
                  isTournament ? `Provisional seeds` : `Registrations`,
                  event.registrations.length
                )}
              </Title>
              <Table>
                <Table.Tbody>
                  {sortedRegistrations &&
                    sortedRegistrations
                      .filter((r) => r.approved)
                      .map((registration, index) => {
                        const member = (event.members as Member[]).find(
                          (m) => m._id === registration.member
                        );
                        // console.log("registration :", registration);
                        return (
                          <Table.Tr key={registration._id}>
                            <Table.Td>
                              <ThemeIcon variant="light">
                                <Text size="xs">{index + 1}</Text>
                              </ThemeIcon>
                            </Table.Td>
                            <Table.Td>
                              {registration.member && (
                                <MemberDisplay
                                  size="sm"
                                  member={registration.member}
                                  avatar
                                />
                              )}
                              {registration.user && !registration.member && (
                                <UserDisplay
                                  size="sm"
                                  user={registration.user}
                                />
                              )}
                            </Table.Td>
                            {showClubs && (
                              <Table.Td>
                                <Text size="xs">{member?.club}</Text>
                              </Table.Td>
                            )}
                            {showCountries && (
                              <Table.Td>
                                <Text size="xl">
                                  <CountryDisplay
                                    country={member?.country}
                                    flag
                                  />
                                </Text>
                              </Table.Td>
                            )}
                            {event.seedType === SeedType.SCORE && (
                              <Table.Td>
                                <ThemeIcon variant="light">
                                  <Text size="xs">
                                    {registration.score || 500}
                                  </Text>
                                </ThemeIcon>
                              </Table.Td>
                            )}
                            {event.seedType === SeedType.USFA && (
                              <Table.Td>
                                <ThemeIcon variant="light">
                                  <Text size="xs">
                                    {registration.usfaRating || "U"}
                                  </Text>
                                </ThemeIcon>
                              </Table.Td>
                            )}
                          </Table.Tr>
                        );
                      })}
                </Table.Tbody>
              </Table>
            </Stack>
          )}
          {isRef && <RegistrationsAdmin event={event} />}
          {isRef && teams && teams.length > 0 && (
            <TeamsInput
              max={teamLimit}
              onChange={onChangeTeams}
              value={teams}
              maxMembers={4}
            />
          )}
          {limited && (
            <PlanPromptBar text="That's the biggest event you can create on your current plan. Upgrade your plan to get more of the action!" />
          )}
          {teamLimited && (
            <PlanPromptBar text="That's the most teams you can have on your current plan. Upgrade your plan to get more of the action!" />
          )}
          <SelfRegistration event={event} board={board} />
          {shareVisible && (
            <Card>
              <Stack>
                <Title order={3} size={13}>
                  {shareTitle}
                </Title>
                <SocialShare board={board} event={event} />
              </Stack>
            </Card>
          )}
        </Stack>
      </Card>
    </Container>
  );
};
export default SummaryScheduled;
